import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { track } from "../lib/mixpanel"

export default class extends Controller {
  static targets = [
    "grid", "widget", "dragHandle", "toolbar",
    "editButton", "editorPanel",
    "userDefaultBtn", "companyDefaultBtn",
    "tabsEditor", "tabsList", "tabStrip"
  ]
  static values = {
    saveUrl: String,
    resetUrl: String,
    editable: Boolean,
    layout: String,
    tabs: String,
    source: String
  }

  // Phase 2.12 Follow-Up 1 (Path B) — same cap as the templates
  // controller's `parse_and_validate_tabs`.
  static MAX_TABS = 8

  connect() {
    this._editMode = false
    this._sortable = null
    this._dirty = false
    this._tabs = this._parseTabsValue()
  }

  _parseTabsValue() {
    if (!this.hasTabsValue || !this.tabsValue) return []
    try {
      const parsed = JSON.parse(this.tabsValue)
      return Array.isArray(parsed) ? parsed.map((t) => ({
        label: String(t.label || t["label"] || ""),
        widget_keys: Array.isArray(t.widget_keys || t["widget_keys"])
          ? (t.widget_keys || t["widget_keys"]).map(String)
          : []
      })) : []
    } catch {
      return []
    }
  }

  disconnect() {
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = null
    }
  }

  // ── Edit mode toggle ──────────────────────────────────────────────

  toggleEditMode() {
    this._editMode = !this._editMode
    if (this._editMode) {
      this._enterEditMode()
      track("Dashboard Edit Mode Entered", { source: this.sourceValue })
    } else {
      this._exitEditMode()
    }
  }

  cancelEditMode() {
    this._editMode = false
    this._dirty = false
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = null
    }
    // Reload to discard any unsaved changes
    window.location.reload()
  }

  _enterEditMode() {
    // Show drag handles on all widgets
    this.dragHandleTargets.forEach(h => {
      h.classList.remove("hidden")
      h.classList.add("flex")
    })

    // Show editor panel (add widget, reset, etc.)
    if (this.hasEditorPanelTarget) {
      this.editorPanelTarget.classList.remove("hidden")
    }

    // Hide the top toolbar button — Done lives in the editor panel now
    this.toolbarTarget.classList.add("hidden")

    // Scroll all the way to the bottom so the user sees the options
    requestAnimationFrame(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })
    })

    // Initialize SortableJS
    this._sortable = Sortable.create(this.gridTarget, {
      handle: ".drag-handle",
      animation: 200,
      ghostClass: "opacity-30",
      forceFallback: true,
      onEnd: () => { this._dirty = true }
    })

    // Path B — populate the Tabs editor from current `_tabs` state
    // each time the user enters edit mode so removed widgets / new
    // tabs reflect the latest layout.
    this._renderTabsEditor()
  }

  _exitEditMode() {
    // Hide drag handles
    this.dragHandleTargets.forEach(h => {
      h.classList.add("hidden")
      h.classList.remove("flex")
    })

    // Hide editor panel
    if (this.hasEditorPanelTarget) {
      this.editorPanelTarget.classList.add("hidden")
    }

    // Show the top toolbar button again
    this.toolbarTarget.classList.remove("hidden")

    // Destroy sortable
    if (this._sortable) {
      this._sortable.destroy()
      this._sortable = null
    }

    // Save if dirty
    if (this._dirty) {
      this._saveLayout()
    }
  }

  // ── Widget actions ────────────────────────────────────────────────

  cycleSize(event) {
    const widget = event.currentTarget.closest("[data-widget-type]")
    if (!widget) return

    const current = parseInt(widget.dataset.widgetColSpan) || 1
    const next = current >= 3 ? 1 : current + 1

    widget.dataset.widgetColSpan = next

    // Update col-span class
    widget.classList.remove("lg:col-span-2", "lg:col-span-3")
    if (next === 2) widget.classList.add("lg:col-span-2")
    if (next === 3) widget.classList.add("lg:col-span-3")

    this._dirty = true
    track("Dashboard Widget Resized", {
      widget_type: widget.dataset.widgetType,
      new_col_span: next
    })
  }

  removeWidget(event) {
    const widget = event.currentTarget.closest("[data-widget-type]")
    if (!widget) return

    const widgetType = widget.dataset.widgetType
    widget.remove()
    this._dirty = true

    // Show the widget in the "Add Widget" panel if it exists
    this._refreshAddWidgetPanel()

    track("Dashboard Widget Removed", { widget_type: widgetType })
  }

  openSettings(event) {
    const widget = event.currentTarget.closest("[data-widget-type]")
    if (!widget) return

    const widgetType = widget.dataset.widgetType
    const schema = JSON.parse(widget.dataset.widgetSettingsSchema || "{}")
    const currentSettings = JSON.parse(widget.dataset.widgetSettings || "{}")

    if (!Object.keys(schema).length) return

    // Build and show settings modal
    this._showSettingsModal(widget, widgetType, schema, currentSettings)
  }

  _showSettingsModal(widget, widgetType, schema, currentSettings) {
    // Remove any existing settings modal
    document.getElementById("dashboard-settings-modal")?.remove()

    const LABELS = {
      urgent_items_count: "Urgent items to show",
      photo_count: "Number of photos",
      entry_count: "Activity entries to show"
    }

    // Build form fields from schema
    let fieldsHtml = ""
    for (const [key, config] of Object.entries(schema)) {
      const value = currentSettings[key] ?? config.default
      const label = LABELS[key] || key.replace(/_/g, " ")
      fieldsHtml += `
        <div class="flex items-center justify-between py-2">
          <label class="text-[13px] text-gray-700 font-medium" for="setting-${key}">${this._escapeHtml(label)}</label>
          <input type="number" id="setting-${key}" name="${key}"
                 value="${value}" min="${config.min}" max="${config.max}" step="1"
                 class="w-20 px-2 py-1 text-[13px] text-gray-700 border border-gray-300 rounded-lg text-right focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500">
        </div>
      `
    }

    const modal = document.createElement("div")
    modal.id = "dashboard-settings-modal"
    modal.className = "fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    modal.innerHTML = `
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4" data-action="click->dashboard-grid#stopModalPropagation">
        <div class="px-5 py-4 border-b border-gray-100">
          <h3 class="text-[15px] font-semibold text-gray-800">Widget Settings</h3>
        </div>
        <div class="px-5 py-3">
          ${fieldsHtml}
        </div>
        <div class="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100">
          <button id="settings-cancel" class="px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">Cancel</button>
          <button id="settings-apply" class="px-3 py-1.5 text-[13px] text-white bg-brand-600 hover:bg-brand-700 rounded-lg cursor-pointer">Apply</button>
        </div>
      </div>
    `

    // Backdrop click closes
    modal.addEventListener("click", (e) => {
      if (e.target === modal) modal.remove()
    })

    // Cancel button
    modal.querySelector("#settings-cancel").addEventListener("click", () => modal.remove())

    // Apply button
    modal.querySelector("#settings-apply").addEventListener("click", () => {
      const newSettings = { ...currentSettings }
      for (const [key, config] of Object.entries(schema)) {
        const input = modal.querySelector(`#setting-${key}`)
        if (input) {
          const val = Math.min(Math.max(parseInt(input.value) || config.default, config.min), config.max)
          newSettings[key] = val
        }
      }
      widget.dataset.widgetSettings = JSON.stringify(newSettings)
      this._dirty = true
      modal.remove()
      track("Dashboard Widget Settings Changed", { widget_type: widgetType, settings: newSettings })
    })

    document.body.appendChild(modal)
  }

  stopModalPropagation(event) {
    event.stopPropagation()
  }

  addWidget(event) {
    const btn = event.currentTarget
    const widgetType = btn.dataset.widgetType
    const colSpan = parseInt(btn.dataset.widgetColSpan) || 1
    const widgetName = btn.dataset.widgetName || widgetType

    // Create a placeholder widget card
    const colClass = colSpan === 3 ? "lg:col-span-3" : colSpan === 2 ? "lg:col-span-2" : ""
    const placeholder = document.createElement("div")
    placeholder.className = `${colClass} bg-white border border-gray-200 rounded-xl`.trim()
    placeholder.dataset.dashboardGridTarget = "widget"
    placeholder.dataset.widgetType = widgetType
    placeholder.dataset.widgetColSpan = colSpan
    placeholder.dataset.widgetSettings = "{}"

    // Add drag handle (visible since we're in edit mode)
    placeholder.innerHTML = `
      <div class="flex items-center justify-between px-4 py-1.5 border-b border-gray-100 bg-gray-50 rounded-t-xl cursor-grab drag-handle"
           data-dashboard-grid-target="dragHandle">
        <div class="flex items-center gap-2">
          <svg class="w-4 h-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"/></svg>
          <span class="text-[13px] text-gray-500 font-medium">${this._escapeHtml(widgetName)}</span>
        </div>
        <div class="flex items-center gap-1">
          <button data-action="click->dashboard-grid#cycleSize" class="p-1 rounded hover:bg-gray-200 text-gray-400 cursor-pointer" data-tip="Change width" data-tip-align="right">
            <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5 1.5 12l6.75 7.5M15.75 4.5 22.5 12l-6.75 7.5"/></svg>
          </button>
          <button data-action="click->dashboard-grid#removeWidget" class="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 cursor-pointer" data-tip="Remove" data-tip-align="right">
            <svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
      <div class="px-5 py-8 flex flex-col items-center justify-center text-center">
        <p class="text-[13px] text-gray-400">Widget will load on save</p>
      </div>
    `

    this.gridTarget.appendChild(placeholder)
    this._dirty = true

    // Hide the add button for this widget
    btn.remove()

    track("Dashboard Widget Added", { widget_type: widgetType })
  }

  // ── Persistence ───────────────────────────────────────────────────

  async _saveLayout() {
    const widgets = this._collectWidgets()
    const tabs = this._collectTabs()

    const tabsError = this._validateTabsClient(tabs, widgets)
    if (tabsError) {
      this._showToast(tabsError, "error")
      return
    }

    try {
      const resp = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken,
          "Accept": "application/json"
        },
        // Path B sends `tabs:` alongside `widgets:`. Server validates
        // tabs via `parse_and_validate_layout_tabs` and persists them
        // to `DashboardLayout.tabs_json` — the resolver surfaces them
        // back through `@layout[:tabs]` so the strip renders on next
        // load even if the user is not on a template that defines tabs.
        body: JSON.stringify({ widgets, tabs })
      })

      if (resp.ok) {
        this._dirty = false
        track("Dashboard Layout Saved", { widget_count: widgets.length, tab_count: tabs.length })

        // Reload to re-render all widgets with server data
        window.location.reload()
      } else {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.error || "Failed to save layout", "error")
      }
    } catch (e) {
      this._showToast("Failed to save layout", "error")
    }
  }

  async resetLayout() {
    try {
      const resp = await fetch(this.resetUrlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": this._csrfToken,
          "Accept": "application/json"
        }
      })

      if (resp.ok) {
        this._showToast("Dashboard reset to default")
        this._dirty = false
        track("Dashboard Layout Reset", { previous_source: this.sourceValue })
        window.location.reload()
      } else {
        this._showToast("Failed to reset layout", "error")
      }
    } catch (e) {
      this._showToast("Failed to reset layout", "error")
    }
  }

  async saveUserDefault() {
    const widgets = this._collectWidgets()
    const btn = this.hasUserDefaultBtnTarget ? this.userDefaultBtnTarget : null
    const url = btn?.dataset.dashboardGridUserDefaultUrlValue

    if (!url) return

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ widgets })
      })

      if (resp.ok) {
        this._showToast("User default layout saved")
        track("Dashboard User Default Saved", { widget_count: widgets.length })
      } else {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.error || "Failed to save user default", "error")
      }
    } catch (e) {
      this._showToast("Failed to save user default", "error")
    }
  }

  async saveCompanyDefault() {
    const widgets = this._collectWidgets()
    const btn = this.hasCompanyDefaultBtnTarget ? this.companyDefaultBtnTarget : null
    const url = btn?.dataset.dashboardGridCompanyDefaultUrlValue

    if (!url) return

    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this._csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ widgets })
      })

      if (resp.ok) {
        this._showToast("Company default layout saved")
        track("Dashboard Company Default Saved", { widget_count: widgets.length })
      } else {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.error || "Failed to save company default", "error")
      }
    } catch (e) {
      this._showToast("Failed to save company default", "error")
    }
  }

  // ── Phase 2.12 Follow-Up 1 (Path B) — tabs editor ─────────────────

  // "+ Add tab" button. Caps at MAX_TABS to mirror the server-side
  // `parse_and_validate_tabs` guard.
  addTab(event) {
    event?.preventDefault()
    if (!this.hasTabsListTarget) return
    const existing = this.tabsListTarget.querySelectorAll("[data-tab-row]").length
    if (existing >= this.constructor.MAX_TABS) {
      this._showToast(`At most ${this.constructor.MAX_TABS} tabs.`, "error")
      return
    }
    const widgets = this._collectWidgets()
    if (!widgets.length) {
      this._showToast("Add widgets to the dashboard before defining tabs.", "error")
      return
    }
    this.tabsListTarget.appendChild(this._buildTabRow({ label: "", widget_keys: [] }, widgets))
    this._dirty = true
    track("Dashboard Tab Created", {
      template_slug: null,
      tab_label: "",
      widget_count: 0,
      surface: "edit_mode"
    })
    this._refreshLivePreview()
  }

  removeTab(event) {
    const row = event.currentTarget.closest("[data-tab-row]")
    const label = (row?.querySelector("[data-tab-label-input]")?.value || "").trim()
    row?.remove()
    this._dirty = true
    track("Dashboard Tab Deleted", { template_slug: null, tab_label: label, surface: "edit_mode" })
    this._refreshLivePreview()
  }

  // Triggered by `change` on label inputs. Path B uses input-text
  // rename rather than contenteditable for accessibility / mobile.
  renameTab(event) {
    const input = event.currentTarget
    const oldLabel = input.dataset.previousLabel || ""
    const newLabel = (input.value || "").trim()
    if (oldLabel === newLabel) return
    input.dataset.previousLabel = newLabel
    this._dirty = true
    if (oldLabel && newLabel) {
      track("Dashboard Tab Renamed", {
        template_slug: null,
        old_label: oldLabel,
        new_label: newLabel,
        surface: "edit_mode"
      })
    }
    this._refreshLivePreview()
  }

  toggleWidgetInTab(event) {
    const cb = event.currentTarget
    const tabLabel = (cb.closest("[data-tab-row]")?.querySelector("[data-tab-label-input]")?.value || "").trim()
    track("Dashboard Widget Tab Assigned", {
      template_slug: null,
      widget_type: cb.value,
      tab_label: tabLabel,
      assigned: cb.checked,
      surface: "edit_mode"
    })
    this._dirty = true
    this._refreshLivePreview()
  }

  _renderTabsEditor() {
    if (!this.hasTabsListTarget) return
    this.tabsListTarget.innerHTML = ""
    const widgets = this._collectWidgets()
    this._tabs.forEach((tab) => {
      this.tabsListTarget.appendChild(this._buildTabRow(tab, widgets))
    })
  }

  _buildTabRow(tab, widgets) {
    const row = document.createElement("div")
    row.dataset.tabRow = ""
    row.className = "border border-gray-200 rounded-md p-2 space-y-2 bg-white"

    const labelRow = document.createElement("div")
    labelRow.className = "flex items-center gap-2"
    const labelEsc = this._escapeHtml(tab.label || "")
    labelRow.innerHTML = `
      <input type="text"
             data-tab-label-input
             data-action="input->dashboard-grid#renameTab"
             value="${labelEsc}"
             maxlength="60"
             placeholder="Tab name"
             class="flex-1 px-2 py-1 text-[13px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" />
      <button type="button"
              data-action="click->dashboard-grid#removeTab"
              class="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
              title="Remove tab">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `
    const labelInput = labelRow.querySelector("[data-tab-label-input]")
    if (labelInput) labelInput.dataset.previousLabel = tab.label || ""
    row.appendChild(labelRow)

    const grid = document.createElement("div")
    grid.className = "flex flex-wrap gap-1.5"
    const assigned = new Set((tab.widget_keys || []).map(String))
    widgets.forEach((w) => {
      const key = String(w.widget_type || "")
      if (!key) return
      const checked = assigned.has(key) ? "checked" : ""
      const id = `tab-widget-${Math.random().toString(36).slice(2, 9)}`
      const chip = document.createElement("label")
      chip.className = "inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 text-[11px] text-gray-700 cursor-pointer has-[:checked]:bg-brand/10 has-[:checked]:border-brand has-[:checked]:text-brand"
      chip.innerHTML = `
        <input type="checkbox"
               id="${id}"
               value="${this._escapeHtml(key)}"
               data-tab-widget-checkbox
               data-action="change->dashboard-grid#toggleWidgetInTab"
               ${checked}
               class="sr-only" />
        <span>${this._escapeHtml(this._widgetTitleFor(key) || key)}</span>
      `
      grid.appendChild(chip)
    })
    row.appendChild(grid)
    return row
  }

  // Reads `data-widget-title` from the live widget wrapper (both
  // project + portfolio dashboards emit it). Falls back to the raw
  // key so the chip always has a label.
  _widgetTitleFor(key) {
    const el = this.element.querySelector(`[data-widget-type="${CSS.escape(key)}"]`)
    return el?.dataset.widgetTitle || null
  }

  _collectTabs() {
    if (!this.hasTabsListTarget) return []
    return Array.from(this.tabsListTarget.querySelectorAll("[data-tab-row]")).map((row) => {
      const label = (row.querySelector("[data-tab-label-input]")?.value || "").trim()
      const widget_keys = Array.from(row.querySelectorAll("[data-tab-widget-checkbox]:checked")).map((cb) => cb.value)
      return { label, widget_keys }
    }).filter((t) => t.label.length > 0)
  }

  _validateTabsClient(tabs, widgets) {
    if (!tabs.length) return null
    if (tabs.length > this.constructor.MAX_TABS) {
      return `Too many tabs (limit ${this.constructor.MAX_TABS}).`
    }
    const labels = tabs.map((t) => t.label.toLowerCase())
    const dup = labels.find((l, i) => labels.indexOf(l) !== i)
    if (dup) return `Duplicate tab name: "${dup}".`
    const widgetKeys = new Set(widgets.map((w) => String(w.widget_type)))
    for (const t of tabs) {
      if (!t.widget_keys.length) {
        return `Tab "${t.label}" has no widgets — assign at least one or remove the tab.`
      }
      const missing = t.widget_keys.filter((k) => !widgetKeys.has(k))
      if (missing.length) {
        return `Tab "${t.label}" references widgets not in this template: ${missing.join(", ")}.`
      }
    }
    return null
  }

  // Live preview — repaints the consumer-side tab strip so the user
  // sees the result of their edits before clicking Done. Only the
  // visible labels matter at this point; widget filtering kicks back
  // in when the user leaves edit mode.
  _refreshLivePreview() {
    if (!this.hasTabStripTarget) return
    const tabs = this._collectTabs()
    if (tabs.length === 0) {
      this.tabStripTarget.innerHTML = ""
      this.tabStripTarget.classList.add("hidden")
      return
    }
    this.tabStripTarget.classList.remove("hidden")
    const allBtn = `<button type="button"
                            data-tab-label="All"
                            class="px-3 py-2 text-[13px] font-medium border-b-2 border-brand text-brand">All</button>`
    const tabBtns = tabs.map((t) => `
      <button type="button"
              data-tab-label="${this._escapeHtml(t.label)}"
              class="px-3 py-2 text-[13px] font-medium text-gray-500">${this._escapeHtml(t.label)}</button>
    `).join("")
    this.tabStripTarget.innerHTML = allBtn + tabBtns
  }

  // ── Helpers ───────────────────────────────────────────────────────

  _collectWidgets() {
    const widgets = []

    this.widgetTargets.forEach((el, index) => {
      const widgetType = el.dataset.widgetType
      if (!widgetType) return

      widgets.push({
        widget_type: widgetType,
        col_span: parseInt(el.dataset.widgetColSpan) || 1,
        position: index,
        settings: JSON.parse(el.dataset.widgetSettings || "{}")
      })
    })

    return widgets
  }

  _refreshAddWidgetPanel() {
    // After removing a widget, we'd need to re-add it to the add panel.
    // For simplicity, the full list is rendered server-side. After save + reload,
    // the panel will be correct. During edit mode, removed widgets are just gone
    // until the user saves and the page reloads.
  }

  get _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
